import React, { useState } from 'react';
import { Clock, TrendingUp, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

// ─── Shell reutilizable ────────────────────────────────────────────────────────
function SectionShell({ icon: Icon, iconCls, title, subtitle, value, isExpanded, onToggle, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={15} className={iconCls} />
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">RD$ {fmt(value)}</span>
          {isExpanded
            ? <ChevronUp size={14} className="text-gray-400" />
            : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="pt-3">{children}</div>
        </div>
      )}
    </div>
  );
}

// ─── Horas Extras ─────────────────────────────────────────────────────────────
export function HorasExtrasSection({ value, detalle, salarioBase, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [details, setDetails] = useState(detalle || {
    ordinarias: { horas: 0, fecha: '' },
    nocturnas:  { horas: 0, fecha: '' },
    feriado:    { horas: 0, fecha: '', diaLibre: false, nocturno: false },
  });

  const horaNormal = salarioBase ? salarioBase / 23.83 / 8 : 0;

  const calculateTotal = () => {
    let total = 0;
    if (details.ordinarias?.horas > 0) total += details.ordinarias.horas * horaNormal * 1.35;
    if (details.nocturnas?.horas > 0)  total += details.nocturnas.horas  * horaNormal * 1.5525;
    if (details.feriado?.horas > 0) {
      let mult = details.feriado.diaLibre ? 2.0 : 1.0;
      if (details.feriado.nocturno) mult += 0.15;
      total += details.feriado.horas * horaNormal * mult;
    }
    return total;
  };

  const handleCalculate = () => onUpdate?.(calculateTotal(), details);

  return (
    <SectionShell
      icon={Clock} iconCls="text-blue-500"
      title="Horas Extras" subtitle="Ordinarias, nocturnas y feriados"
      value={value} isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">

        {/* Info hora normal */}
        <p className="text-xs text-gray-400 bg-gray-50 px-3 py-2 rounded-lg">
          Hora normal: <span className="font-semibold text-gray-700">RD$ {horaNormal.toFixed(4)}</span>
          {' · '}Salario base: <span className="font-semibold text-gray-700">RD$ {fmt(salarioBase)}</span>
        </p>

        {/* Ordinarias */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Ordinarias <span className="text-gray-400 font-normal">(+35%)</span>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Horas</label>
              <input
                type="number" step="0.5" min="0"
                value={details.ordinarias?.horas || 0}
                onChange={(e) => setDetails({
                  ...details,
                  ordinarias: { ...details.ordinarias, horas: parseFloat(e.target.value) || 0 }
                })}
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">
                = RD$ {((details.ordinarias?.horas || 0) * horaNormal * 1.35).toFixed(2)}
              </p>
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input
                type="date"
                value={details.ordinarias?.fecha || ''}
                onChange={(e) => setDetails({
                  ...details,
                  ordinarias: { ...details.ordinarias, fecha: e.target.value }
                })}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* Nocturnas */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Nocturnas <span className="text-gray-400 font-normal">(+55%)</span>
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Horas</label>
              <input
                type="number" step="0.5" min="0"
                value={details.nocturnas?.horas || 0}
                onChange={(e) => setDetails({
                  ...details,
                  nocturnas: { ...details.nocturnas, horas: parseFloat(e.target.value) || 0 }
                })}
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">
                = RD$ {((details.nocturnas?.horas || 0) * horaNormal * 1.5525).toFixed(2)}
              </p>
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input
                type="date"
                value={details.nocturnas?.fecha || ''}
                onChange={(e) => setDetails({
                  ...details,
                  nocturnas: { ...details.nocturnas, fecha: e.target.value }
                })}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* Feriado */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Feriado <span className="text-gray-400 font-normal">(×2 si es día libre)</span>
          </p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className={labelCls}>Horas</label>
              <input
                type="number" step="0.5" min="0"
                value={details.feriado?.horas || 0}
                onChange={(e) => setDetails({
                  ...details,
                  feriado: { ...details.feriado, horas: parseFloat(e.target.value) || 0 }
                })}
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">
                {(() => {
                  let mult = details.feriado?.diaLibre ? 2.0 : 1.0;
                  if (details.feriado?.nocturno) mult += 0.15;
                  return `= RD$ ${((details.feriado?.horas || 0) * horaNormal * mult).toFixed(2)}`;
                })()}
              </p>
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input
                type="date"
                value={details.feriado?.fecha || ''}
                onChange={(e) => setDetails({
                  ...details,
                  feriado: { ...details.feriado, fecha: e.target.value }
                })}
                className={inputCls}
              />
            </div>
          </div>
          {(details.feriado?.horas || 0) > 0 && (
            <div className="space-y-1.5 mt-2">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={details.feriado?.diaLibre || false}
                  onChange={(e) => setDetails({
                    ...details,
                    feriado: { ...details.feriado, diaLibre: e.target.checked }
                  })}
                  className="rounded"
                />
                ¿Es su día libre? (×2)
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={details.feriado?.nocturno || false}
                  onChange={(e) => setDetails({
                    ...details,
                    feriado: { ...details.feriado, nocturno: e.target.checked }
                  })}
                  className="rounded"
                />
                ¿Horario nocturno? (+15%)
              </label>
            </div>
          )}
        </div>

        {/* Calcular */}
        <button
          type="button"
          onClick={handleCalculate}
          className="w-full py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          Calcular Total
        </button>

        <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-sm text-gray-600">Total calculado:</span>
          <span className="text-sm font-bold text-blue-600">RD$ {fmt(calculateTotal())}</span>
        </div>

      </div>
    </SectionShell>
  );
}

// ─── Comisiones ───────────────────────────────────────────────────────────────
export function ComisionesSection({ value, detalle, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [comisiones, setComisiones] = useState(
    detalle || [{ id: Date.now(), concepto: '', monto: 0 }]
  );

  const getTotal = (list) =>
    list.reduce((s, c) => s + parseFloat(c.monto || 0), 0);

  const handleAdd = () => {
    const updated = [...comisiones, { id: Date.now(), concepto: '', monto: 0 }];
    setComisiones(updated);
  };

  const handleRemove = (id) => {
    if (comisiones.length > 1) {
      const updated = comisiones.filter(c => c.id !== id);
      setComisiones(updated);
      onUpdate?.(getTotal(updated), updated);
    }
  };

  const handleChange = (id, field, val) => {
    const updated = comisiones.map(c => c.id === id ? { ...c, [field]: val } : c);
    setComisiones(updated);
    onUpdate?.(getTotal(updated), updated);
  };

  return (
    <SectionShell
      icon={TrendingUp} iconCls="text-green-500"
      title="Comisiones" subtitle="Múltiples conceptos de comisión"
      value={value} isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">

        {comisiones.map((com, index) => (
          <div key={com.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">Comisión #{index + 1}</p>
              {comisiones.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemove(com.id)}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Concepto</label>
                <input
                  type="text" value={com.concepto}
                  onChange={(e) => handleChange(com.id, 'concepto', e.target.value)}
                  placeholder="Ej: Comisión por ventas"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Monto (RD$)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={com.monto}
                  onChange={(e) => handleChange(com.id, 'monto', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={handleAdd}
          className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center justify-center gap-1.5"
        >
          <Plus size={13} />
          Agregar comisión
        </button>

        <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-sm text-gray-600">Total comisiones:</span>
          <span className="text-sm font-bold text-gray-900">RD$ {fmt(getTotal(comisiones))}</span>
        </div>

      </div>
    </SectionShell>
  );
}
